/*
  Coordinates every add-on on the product page (gift wrapping, personalization,
  anything added later) into ONE cart request.

  WHY THIS EXISTS
  Each add-on used to intercept the product form's submit itself. With two of
  them the interceptors raced: both would stop the same submit, each would
  re-submit, and the item could be added twice or the add-on dropped entirely.
  There is now exactly one interceptor — this file — and add-ons contribute
  items to it instead of touching the form.

  HOW AN ADD-ON PARTICIPATES
  Put `data-cart-addon` and `data-form-id="<product form id>"` on the element,
  and give it a `getAddonItem(form)` method returning either null (nothing to
  add this time) or a cart line: { id, quantity, properties }.

  TIMING
  We listen on the document in the CAPTURE phase so we run before Dawn's own
  submit handler. Add-ons are added first, then the form is re-submitted and
  Dawn adds the item itself — so the cart opens once, already complete, rather
  than flickering through a half-filled state.

  ACCELERATED CHECKOUT (added 27 Aug 2026)
  Shopify's "Buy it now" / Shop Pay button does NOT fire the form's submit
  event, so nothing in here ever sees it. It hands the form's own fields — the
  "Gift wrapping: Yes" flag included — straight to an accelerated checkout, so
  the order promises wrapping that was never added and never charged. That is
  what happened on #2204, and it is why gift wrapping had a 0-for-5 record.
  While any add-on on a form is ticked, the accelerated buttons for that form
  are hidden and a short note points the shopper at Add to cart. Untick the
  box and they come straight back. The note is worded positively — it tells
  the shopper what to do, not what the hidden button cannot do (Po, 31 Aug).

  FAILURE POLICY — REVERSED 27 Aug 2026, DO NOT PUT IT BACK
  This file used to add the item even when the add-on request failed, on the
  reasoning that a broken add-on must never cost a sale. Po's explicit call on
  27 Aug 2026: whatever the shopper selected must be charged in full. An
  uncharged gift wrap is not merely lost revenue — the wrapping still gets
  made, so the materials and the labour are given away. So a failed add-on now
  blocks the entire add to cart. Nothing enters the cart, the shopper gets an
  error on the product form and can try again.

  There is deliberately no automatic retry. A non-OK response means Shopify
  rejected the whole items array and added nothing, so retrying would only be
  rejected again; a thrown fetch means we cannot know whether the cart took it,
  and a blind retry there risks charging for two wraps. One honest error beats
  a silent double charge.

  If someone later reports "add to cart sometimes does nothing", this is why —
  check the console for [cart-addons] before assuming it is a bug.

  WHAT IS NOT IN HERE, AND WHY (27 Aug 2026)
  A live product-page price total (base + ticked add-ons) was built and shipped
  on 27 Aug 2026 and had to be rolled back the same day: it froze the product
  page on mobile, killing the checkboxes and the variant swatches with it. It
  watched the price element for changes so it could re-apply its figure after
  Dawn re-rendered, and it located that element with a fallback selector that
  had never been verified against this theme. When the fallback matched a
  moving target, every callback attached another watcher, and the watchers
  multiplied until the page stopped responding.

  The lesson is not "never touch the price". It is that anything reacting to
  DOM changes here must resolve to ONE verified, stable element, and must be
  previewed on a phone before it is published. Prefer an element we render and
  own outright over fighting Dawn for its own.
*/
(function () {
  var passthrough = new WeakSet();
  if (window.__cartAddonsLoaded) return;
  window.__cartAddonsLoaded = true;

  var STYLE_ID = 'cart-addons-style';
  var ADDON_ERROR =
    'We could not add your gift wrapping or personalization just now, so nothing was added to your cart. Please check your connection and try Add to cart again.';
  var ADDON_CHECKOUT_NOTE =
    'Please use Add to cart to check out, so your gift wrapping and personalization come with your order.';

  function addUrl() {
    var base = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';
    return base.indexOf('.js') === -1 ? base + '.js' : base;
  }

  function addonElements(form) {
    var id = form.getAttribute('id');
    return Array.prototype.filter.call(document.querySelectorAll('[data-cart-addon]'), function (el) {
      return el.dataset.formId === id;
    });
  }

  function collect(form) {
    var items = [];
    addonElements(form).forEach(function (el) {
      if (typeof el.getAddonItem !== 'function') return;
      try {
        var item = el.getAddonItem(form);
        if (item) items.push(item);
      } catch (e) {
        console.error('[cart-addons] add-on failed to contribute', el, e);
      }
    });
    return items;
  }

  // True as soon as a box is ticked — deliberately NOT the same question as
  // collect(), which waits for personalization text before it will contribute a
  // cart line. For hiding express checkout, intent is enough.
  function anyAddonTicked(form) {
    return addonElements(form).some(function (el) {
      var box = el.querySelector('input[type="checkbox"]');
      return !!box && box.checked;
    });
  }

  /* --- Telling the shopper, using Dawn's own error slot ---------------- */

  function errorParts(form) {
    var host = form.closest('product-form');
    if (!host) return null;
    var wrapper = host.querySelector('.product-form__error-message-wrapper');
    var message = host.querySelector('.product-form__error-message');
    if (!wrapper || !message) return null;
    return { wrapper: wrapper, message: message };
  }

  function showFormError(form) {
    var parts = errorParts(form);
    if (!parts) {
      window.alert(ADDON_ERROR);
      return;
    }
    parts.message.textContent = ADDON_ERROR;
    parts.wrapper.hidden = false;
    parts.wrapper.dataset.cartAddonsError = 'true';
  }

  // Only ever clears OUR message — Dawn puts its own sold-out and quantity
  // errors in the same slot and those are not ours to dismiss.
  function clearFormError(form) {
    var parts = errorParts(form);
    if (!parts || parts.wrapper.dataset.cartAddonsError !== 'true') return;
    parts.message.textContent = '';
    parts.wrapper.hidden = true;
    delete parts.wrapper.dataset.cartAddonsError;
  }

  /* --- Accelerated checkout guard -------------------------------------- */

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.product-form__buttons.cart-addons--active .shopify-payment-button,',
      '.product-form__buttons.cart-addons--active shopify-accelerated-checkout,',
      '.product-form__buttons.cart-addons--active shopify-accelerated-checkout-cart',
      '{display:none !important;}',
      '.product-form__addon-note{display:none;flex:0 0 100%;width:100%;margin:0.8rem 0 0;',
      'font-size:1.3rem;line-height:1.4;text-align:center;opacity:0.75;}',
      '.product-form__buttons.cart-addons--active .product-form__addon-note{display:block;}'
    ].join('');
    document.head.appendChild(style);
  }

  function syncForm(form) {
    var buttons = form.querySelector('.product-form__buttons');
    if (!buttons) return;

    var active = anyAddonTicked(form);
    clearFormError(form);

    if (active) {
      ensureStyle();
      if (!buttons.querySelector('[data-addon-checkout-note]')) {
        var note = document.createElement('p');
        note.setAttribute('data-addon-checkout-note', '');
        note.className = 'product-form__addon-note';
        note.textContent = ADDON_CHECKOUT_NOTE;
        buttons.appendChild(note);
      }
    }

    // The class goes on the container, not the button, because Shopify injects
    // the accelerated button asynchronously; a CSS rule catches it whenever it
    // lands, whereas a direct style.display would miss a late arrival.
    buttons.classList.toggle('cart-addons--active', active);
  }

  // Event-driven only. Nothing in this file observes the DOM or runs on a
  // timer; see WHAT IS NOT IN HERE above before adding anything that does.
  function syncAll() {
    document.querySelectorAll('form[id^="product-form-"]').forEach(syncForm);
  }

  /* --- The single submit interceptor ------------------------------------ */

  document.addEventListener(
    'submit',
    async function (event) {
      var form = event.target;
      if (!form || !form.getAttribute('id') || form.getAttribute('id').indexOf('product-form-') !== 0) return;

      // Second pass: add-ons are already in the cart, let Dawn handle this one.
      if (passthrough.has(form)) {
        passthrough.delete(form);
        return;
      }

      var items = collect(form);
      if (!items.length) return;

      event.preventDefault();
      event.stopPropagation();
      clearFormError(form);

      var added = false;
      var reason = '';

      try {
        var response = await fetch(addUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (response.ok) {
          added = true;
        } else {
          reason = 'cart rejected the add-ons, status ' + response.status + ': ' + (await response.text());
        }
      } catch (e) {
        reason = 'the add-on request never completed: ' + e;
      }

      // See FAILURE POLICY at the top. Blocking here is the whole point: the
      // item must not reach the cart without the add-on it was paired with.
      if (!added) {
        console.error('[cart-addons] add to cart BLOCKED —', reason);
        showFormError(form);
        return;
      }

      passthrough.add(form);
      form.requestSubmit();
    },
    true
  );

  // Shared helpers for add-on elements.
  window.CartAddons = {
    quantity: function (form) {
      var input = form.querySelector('[name="quantity"]');
      var n = input ? parseInt(input.value, 10) : 1;
      return isNaN(n) || n < 1 ? 1 : n;
    },

    // "Sea Flap Flap — Turquoise / Airtag holder", so an add-on line can say
    // which item in a multi-item order it belongs to.
    selectionLabel: function (sectionId, productTitle) {
      var parts = [];
      var picker = document.querySelector(
        'variant-radios[data-section="' + sectionId + '"], variant-selects[data-section="' + sectionId + '"]'
      );

      if (picker) {
        var fieldsets = picker.querySelectorAll('fieldset');
        if (fieldsets.length) {
          fieldsets.forEach(function (fs) {
            var checked = fs.querySelector('input[type="radio"]:checked');
            if (checked) parts.push(checked.value);
          });
        } else {
          picker.querySelectorAll('select').forEach(function (sel) {
            if (sel.value) parts.push(sel.value);
          });
        }
      }

      return parts.length ? productTitle + ' \u2014 ' + parts.join(' / ') : productTitle;
    }
  };

  // Wired only after CartAddons exists, since collect() leans on it.
  document.addEventListener('change', syncAll);
  document.addEventListener('shopify:section:load', syncAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
})();
